import {
  BookingFollowUp,
  BookingFollowUpStatus,
  BookingFollowUpType,
  Employee,
} from "../../process/types/processTypes";

export const FOLLOW_UP_STATUS_ORDER: BookingFollowUpStatus[] = [
  BookingFollowUpStatus.new,
  BookingFollowUpStatus.booked_with_date,
  BookingFollowUpStatus.confirmed_by_employee,
  BookingFollowUpStatus.employee_completed,
];

const EMBASSY_STATUS_ORDER: BookingFollowUpStatus[] = [
  BookingFollowUpStatus.new,
  BookingFollowUpStatus.embassy_in_progress,
  BookingFollowUpStatus.embassy_returned,
  BookingFollowUpStatus.embassy_stamped,
];

export function statusOrderForType(
  type: BookingFollowUpType,
): BookingFollowUpStatus[] {
  return type === BookingFollowUpType.embassy
    ? EMBASSY_STATUS_ORDER
    : FOLLOW_UP_STATUS_ORDER;
}

export function terminalStatusForType(
  type: BookingFollowUpType,
): BookingFollowUpStatus {
  return type === BookingFollowUpType.embassy
    ? BookingFollowUpStatus.embassy_stamped
    : BookingFollowUpStatus.employee_completed;
}

/**
 * Target statuses a manager may move an embassy follow-up to from its current
 * status. Embassy is non-linear: "returned for fixing" loops back to in
 * progress. Returns [] once stamped (terminal).
 */
export function embassyTargetStatuses(
  current: BookingFollowUpStatus,
): BookingFollowUpStatus[] {
  switch (current) {
    case BookingFollowUpStatus.embassy_in_progress:
      return [
        BookingFollowUpStatus.embassy_returned,
        BookingFollowUpStatus.embassy_stamped,
      ];
    case BookingFollowUpStatus.embassy_returned:
      return [
        BookingFollowUpStatus.embassy_in_progress,
        BookingFollowUpStatus.embassy_stamped,
      ];
    case BookingFollowUpStatus.embassy_stamped:
      return [];
    default:
      // new (or any pre-submission state): documents sent to embassy
      return [BookingFollowUpStatus.embassy_in_progress];
  }
}

export function followUpFieldForType(
  type: BookingFollowUpType,
): "medicalFollowUp" | "tasheerFollowUp" | "embassyFollowUp" | "flightFollowUp" {
  switch (type) {
    case BookingFollowUpType.medical:
      return "medicalFollowUp";
    case BookingFollowUpType.tasheer:
      return "tasheerFollowUp";
    case BookingFollowUpType.embassy:
      return "embassyFollowUp";
    case BookingFollowUpType.flight:
      return "flightFollowUp";
  }
}

export function getFollowUpForType(
  employee: Employee,
  type: BookingFollowUpType,
): BookingFollowUp | undefined {
  return employee[followUpFieldForType(type)];
}

export function effectiveFollowUpStatus(
  followUp?: BookingFollowUp | null,
): BookingFollowUpStatus {
  return followUp?.status ?? BookingFollowUpStatus.new;
}

/**
 * Next status in a linear ladder. Embassy is non-linear (a branch), so it has
 * no single "next" — callers use {@link embassyTargetStatuses} instead.
 */
export function nextFollowUpStatus(
  current: BookingFollowUpStatus,
  type: BookingFollowUpType,
): BookingFollowUpStatus | null {
  if (type === BookingFollowUpType.embassy) return null;
  const order = statusOrderForType(type);
  const idx = order.indexOf(current);
  if (idx < 0 || idx >= order.length - 1) return null;
  return order[idx + 1];
}

export function isForwardFollowUpTransition(
  type: BookingFollowUpType,
  from: BookingFollowUpStatus,
  to: BookingFollowUpStatus,
): boolean {
  if (type === BookingFollowUpType.embassy) {
    return from !== BookingFollowUpStatus.embassy_stamped;
  }
  const order = statusOrderForType(type);
  const fromIdx = order.indexOf(from);
  const toIdx = order.indexOf(to);
  if (fromIdx < 0 || toIdx < 0) return false;
  return toIdx >= fromIdx;
}

/** Returns the reason a target status cannot be applied, or `null` if eligible. */
export function ineligibleFollowUpReason(
  targetStatus: BookingFollowUpStatus,
  employee: Employee,
  type: BookingFollowUpType,
): string | null {
  const current = effectiveFollowUpStatus(getFollowUpForType(employee, type));
  if (!isForwardFollowUpTransition(type, current, targetStatus)) {
    return "status cannot move backwards";
  }
  return null;
}

export function countForFollowUpType(
  counts:
    | { type: BookingFollowUpType; byStatus: { status: string; count: number }[] }[]
    | undefined,
  type: BookingFollowUpType,
  status?: BookingFollowUpStatus | "results",
): number {
  const row = counts?.find((c) => c.type === type);
  if (!row) return 0;
  if (!status) {
    const terminal = terminalStatusForType(type);
    return row.byStatus
      .filter((s) => s.status !== terminal && s.status !== "results")
      .reduce((sum, s) => sum + s.count, 0);
  }
  return row.byStatus.find((s) => s.status === status)?.count ?? 0;
}

export const WAFID_BOOKING_URL = "https://www.wafid.com/book-appointment/";
export const TASHEER_URL = "https://agents.tasheer.com/";

/** Medical Completed pill: booking done, still awaiting FIT/UNFIT. */
export const MEDICAL_AWAITING_RESULT_STATUSES = [
  "completed",
  "record_not_found",
] as const;

/** Medical Results pill: WAFID outcome known. */
export const MEDICAL_RESULT_STATUSES = ["fit", "unfit"] as const;

export const WAFID_BULK_CHECK_LIMIT = 25;
